// The frontend half of the job's experience range. Mirrors the backend
// (server/app/api/v2/job/schema/api.py, JobCreateRequest) so both layers
// agree on the same rule: minimum required, maximum optional, and a
// maximum below the minimum is refused.
import { EXPERIENCE_REGEX } from "@/config/constants";
import { z } from "zod";

const EXPERIENCE_FORMAT_MESSAGE = "Enter valid experience (e.g. 1, 5.9, 12, 12.11, max 100 years)";

export const EXPERIENCE_MESSAGES = {
  minRequired: "Minimum experience is required",
  maxBelowMin: "Maximum cannot be less than minimum",
};

// Required. One superRefine rather than chained refines, so a blank field
// reports "required" alone instead of "required" and "invalid" together.
export const experienceMinSchema = z
  .string()
  .transform((v) => (v ?? "").trim())
  .superRefine((v, ctx) => {
    if (!v) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: EXPERIENCE_MESSAGES.minRequired });
      return;
    }
    if (!EXPERIENCE_REGEX.test(v)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: EXPERIENCE_FORMAT_MESSAGE });
    }
  });

// Optional — blank stays blank and becomes null on the wire.
export const experienceMaxSchema = z
  .string()
  .transform((v) => (v ?? "").trim())
  .refine((v) => !v || EXPERIENCE_REGEX.test(v), { message: EXPERIENCE_FORMAT_MESSAGE });

/**
 * The cross-field rule, embedded in the job form's schema-level superRefine.
 * The error attaches to experienceMax, which is the field the user changes to
 * clear it.
 *
 * Guarded on both ends parsing to finite numbers: zod runs a schema-level
 * superRefine even when a field-level rule already failed, so this sees a
 * blank or malformed minimum and must say nothing about it.
 */
export function validateJobExperienceRange({ experienceMin, experienceMax }, ctx) {
  if (!experienceMin || !experienceMax) return;
  const min = Number(experienceMin);
  const max = Number(experienceMax);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return;
  if (max < min) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["experienceMax"],
      message: EXPERIENCE_MESSAGES.maxBelowMin,
    });
  }
}
